import { ChevronDown, X, Paperclip, ArrowUp, Square, Database, Upload } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { filesystemService } from "../../utils/filesystem";
import { pyodideService } from "@/services/PyodideService";
import { toast } from "sonner";
import { isBinaryFile } from "../../lib/fileUtils";

interface ModelOption {
    name: string;
    value: string;
    provider: string;
    providerTitle: string;
}

interface PromptInputProps {
    onSubmit: (prompt: string) => void;
    isLoading: boolean;
    onStopGeneration: () => void;
    selectedModel?: string;
    onModelSelect?: (model: string) => void;
    modelOptions?: ModelOption[];
    showModelSelection?: boolean;
    showDataUpload?: boolean;
    minHeight?: string;
    maxHeight?: string;
    defaultRows?: number;
    placeholder?: string;
    className?: string;
    textareaClassName?: string;
    buttonClassName?: string;
    modelSelectorClassName?: string;
    fileSelectorClassName?: string;
    borderColor?: string;
}

interface FileSelectorProps {
    selectedFiles: string[];
    onFilesChange: (files: string[]) => void;
    className?: string;
}

const FileSelector: FC<FileSelectorProps> = ({ selectedFiles, onFilesChange, className }) => {
    const [isFileDropdownOpen, setIsFileDropdownOpen] = useState(false);
    const [availableFiles, setAvailableFiles] = useState<string[]>([]);
    const fileDropdownRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const loadFiles = async () => {
            if (isFileDropdownOpen) {
                try {
                    const files = await pyodideService.listFilesInDirectory("/project/data");
                    setAvailableFiles(files.filter(file => file.endsWith('.csv')));
                } catch (error) {
                    console.error("Error loading files:", error);
                }
            }
        };

        loadFiles();
    }, [isFileDropdownOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (fileDropdownRef.current && !fileDropdownRef.current.contains(event.target as Node)) {
                setIsFileDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement> | DragEvent) => {
        let selectedFiles: FileList | null = null;

        if (event instanceof DragEvent) {
            selectedFiles = event.dataTransfer?.files || null;
        } else {
            selectedFiles = event.target.files;
        }

        if (!selectedFiles || selectedFiles.length === 0) return;

        try {
            await filesystemService.createDirectory('/project/data');
            const filesToUpload: Record<string, string | ArrayBuffer> = {};
            const FILE_SIZE_LIMIT = 10 * 1024 * 1024;

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                if (file.size > FILE_SIZE_LIMIT) {
                    toast.error(
                        `File ${file.name} exceeds the 10MB limit. For larger datasets, please contact sales.`,
                        {
                            action: {
                                label: "Schedule",
                                onClick: () => window.open("https://cal.com/amruthagujjar", "_blank")
                            },
                            duration: 6000,
                        }
                    );
                    continue;
                }

                const filePath = `/project/data/${file.name}`;
                try {
                    if (isBinaryFile(file.name)) {
                        const buffer = await file.arrayBuffer();
                        filesToUpload[filePath] = buffer;
                    } else {
                        const content = await file.text();
                        filesToUpload[filePath] = content;
                    }
                } catch (error) {
                    console.error(`Error preparing file ${file.name}:`, error);
                }
            }

            if (Object.keys(filesToUpload).length > 0) {
                if (pyodideService.isReady()) {
                    await pyodideService.loadFiles(filesToUpload);
                } else {
                    for (const [path, content] of Object.entries(filesToUpload)) {
                        filesystemService.pendingFiles[path] = content;
                    }
                }
            }

            const files = await pyodideService.listFilesInDirectory("/project/data");
            setAvailableFiles(files.filter(file => file.endsWith('.csv')));
        } catch (error) {
            console.error('Error during file upload:', error);
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <div className={cn("flex items-center gap-2", className)}>
            <div className="relative" ref={fileDropdownRef}>
                <div
                    className="flex items-center justify-center px-1 text-gray-700 rounded-md text-[10px] h-6 cursor-pointer transition-colors hover:text-gray-900 hover:bg-gray-100 bg-transparent border-none shadow-none"
                    onClick={() => setIsFileDropdownOpen(!isFileDropdownOpen)}
                >
                    <Paperclip className="w-3 h-3" />
                    {selectedFiles.length === 0 && (
                        <span className="ml-1">Add data</span>
                    )}
                </div>
                {isFileDropdownOpen && (
                    <div className="absolute left-0 bottom-full mb-1 bg-white shadow-lg rounded-md border border-gray-200 py-1 w-[220px] z-10">
                        <div className="px-3 py-2">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    fileInputRef.current?.click();
                                }}
                                className="flex items-center gap-2 w-full px-2 py-1.5 text-[10px] text-gray-700 hover:bg-gray-50 rounded transition-colors"
                            >
                                <Upload className="w-3 h-3" />
                                <span>Upload files</span>
                            </button>
                        </div>
                        {availableFiles.length > 0 && (
                            <div className="border-t border-gray-200 mt-1 pt-1">
                                <div className="px-3 py-1 text-[10px] text-gray-500">Available files</div>
                                {availableFiles.map((file) => (
                                    <label
                                        key={file}
                                        className="flex items-center gap-2 px-3 py-1.5 text-[10px] cursor-pointer hover:bg-gray-50 transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedFiles.includes(file)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    onFilesChange([...selectedFiles, file]);
                                                } else {
                                                    onFilesChange(selectedFiles.filter(f => f !== file));
                                                }
                                            }}
                                            className="rounded border-gray-300 accent-gray-500 focus:ring-gray-400"
                                        />
                                        <span className="text-gray-700 truncate">{file}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                multiple
                className="hidden"
            />
            {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedFiles.map((file) => (
                        <div
                            key={file}
                            className="group flex items-center px-1 text-gray-700 rounded-md text-[10px] relative h-6 hover:bg-gray-100 bg-transparent border-none shadow-none"
                        >
                            <span className="flex items-center justify-center w-4 h-4 mr-0.5 relative">
                                <button
                                    type="button"
                                    onClick={() => onFilesChange(selectedFiles.filter(f => f !== file))}
                                    className="absolute inset-0 w-4 h-4 flex items-center justify-center rounded-full text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                    title="Remove file"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                                <Database className="w-3 h-3 text-gray-700 opacity-100 group-hover:opacity-0 transition-opacity" />
                            </span>
                            <span className="truncate max-w-[150px] ml-0.5">{file}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

interface ModelSelectorProps {
    selectedModel: string;
    onModelSelect: (model: string) => void;
    modelOptions: ModelOption[];
    className?: string;
}

const ModelSelector: FC<ModelSelectorProps> = ({ selectedModel, onModelSelect, modelOptions, className }) => {
    const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
    const modelDropdownRef = useRef<HTMLDivElement>(null);
    const currentModel = modelOptions.find(m => m.value === selectedModel) || modelOptions[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
                setIsModelDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className={cn("relative", className)} ref={modelDropdownRef}>
            <div
                className="flex items-center px-1 h-6 text-gray-700 rounded-md text-[10px] cursor-pointer transition-colors hover:text-gray-900 hover:bg-gray-100 bg-transparent border-none shadow-none"
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
            >
                <span className="font-medium">{currentModel.name}</span>
                <ChevronDown className={cn("w-2.5 h-2.5 ml-1 transition-transform", isModelDropdownOpen ? "rotate-180" : "")}/>
            </div>
            {isModelDropdownOpen && (
                <div className="absolute left-0 bottom-full mb-1 bg-white shadow-lg rounded-md border border-gray-200 py-1 w-[220px] z-10">
                    {modelOptions.map((model) => (
                        <div
                            key={model.value}
                            className={cn(
                                "px-3 py-1.5 text-xs cursor-pointer hover:bg-gray-50 transition-colors flex justify-between items-center",
                                model.value === selectedModel ? "bg-gray-50 font-medium text-gray-900" : "text-gray-700"
                            )}
                            onClick={() => onModelSelect(model.value)}
                        >
                            <span className="flex items-center gap-1.5">
                                {model.value === selectedModel && (
                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                )}
                                {model.name}
                            </span>
                            <span className="text-xs text-gray-500 capitalize">{model.providerTitle}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const PromptInput: FC<PromptInputProps> = ({
    onSubmit,
    isLoading,
    onStopGeneration,
    selectedModel,
    onModelSelect,
    modelOptions,
    showModelSelection = true,
    showDataUpload = true,
    minHeight = '32px',
    maxHeight = '120px',
    defaultRows = 1,
    placeholder = "Query, chart, and magic sparks...",
    className,
    textareaClassName,
    buttonClassName,
    modelSelectorClassName,
    fileSelectorClassName,
    borderColor = 'border-gray-400'
}) => {
    const [input, setInput] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        const userMessage = input.trim();
        if (!userMessage || isLoading) return;

        onSubmit(userMessage);
        setInput('');
    }, [input, isLoading, onSubmit]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
    }, []);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    }, [handleSubmit]);

    return (
        <form onSubmit={handleSubmit} className={cn("flex-none p-3 bg-transparent", className)}>
            <div className={cn("flex flex-col gap-2 bg-white border-1 rounded-lg p-2", borderColor)}>
                {showDataUpload && (
                    <FileSelector
                        selectedFiles={selectedFiles}
                        onFilesChange={setSelectedFiles}
                        className={fileSelectorClassName}
                    />
                )}
                <textarea
                    placeholder={placeholder}
                    className={cn(
                        "flex-grow bg-transparent border-none focus:ring-0 focus:outline-none resize-none text-xs placeholder:text-gray-400 text-gray-900 px-2 w-full",
                        textareaClassName
                    )}
                    rows={defaultRows}
                    disabled={isLoading}
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    style={{ minHeight, maxHeight }}
                />
                <div className="flex items-center justify-between w-full">
                    <div className="flex-1">
                        {showModelSelection && (
                            <ModelSelector
                                selectedModel={selectedModel || ''}
                                onModelSelect={onModelSelect || (() => {})}
                                modelOptions={modelOptions || []}
                                className={modelSelectorClassName}
                            />
                        )}
                    </div>
                    {isLoading ? (
                        <button
                            type="button"
                            onClick={onStopGeneration}
                            className={cn(
                                "p-1 rounded-full bg-black text-white hover:bg-gray-800 transition-colors ml-2",
                                buttonClassName
                            )}
                        >
                            <Square className="w-3 h-3" />
                        </button>
                    ) : (
                        <button
                            type="submit"
                            className={cn(
                                "p-1 rounded-full transition-colors ml-2",
                                !input.trim()
                                    ? "bg-gray-300 cursor-not-allowed"
                                    : "bg-black hover:bg-gray-800 text-white",
                                buttonClassName
                            )}
                            disabled={!input.trim()}
                        >
                            <ArrowUp className="w-3 h-3" />
                        </button>
                    )}
                </div>
            </div>
        </form>
    );
};

export default PromptInput; 